import { ApiService } from '../ApiService';
import type { ApiResponse } from '../types';
import type { User, UserAccessLevel, UserBusinessUnitAccess } from '../../../types/auth';
import { CurrencyCode } from '../../../config/currency';

/**
 * Préférences utilisateur conformément à la documentation API
 */
export interface UserPreferences {
  language: 'fr' | 'en';
  currency: CurrencyCode;
  theme: 'light' | 'dark';
}

/**
 * Interface utilisateur étendue conforme à la documentation API
 * Alignée avec le backend User entity
 */
export interface UserWithDetails extends User {
  // Informations additionnelles
  department?: string;
  position?: string;
  phone?: string;
  lastLogin: string;
  status: 'active' | 'inactive' | 'pending';
  avatar?: string;
  auth0Id?: string;
  
  // Permissions et préférences
  permissions?: string[];
  preferences?: UserPreferences;
  
  // Organisation et unité d'affaires
  organizationId?: string;
  businessUnitId?: string;
  businessUnitType?: 'company' | 'branch' | 'pos';
  
  // Champs legacy (pour compatibilité)
  /** @deprecated Utiliser businessUnitType */
  accessLevel?: UserAccessLevel;
  /** @deprecated Utiliser businessUnitId */
  primaryBusinessUnitId?: string;
  
  // Métadonnées
  invitationSent?: boolean;
  createdAt?: string;
  lastLoginAt?: string;
}

/**
 * Statistiques des utilisateurs
 */
export interface UsersStats {
  total: number;
  byRole: {
    admin: number;
    accountant: number;
    viewer: number;
  };
  byStatus: {
    active: number;
    inactive: number;
    pending: number;
  };
  lastLogin: string;
}

/**
 * Permission disponible dans le système
 */
export interface AvailablePermission {
  key: string;
  name: string;
  description: string;
  category: string;
}

/**
 * Rôles autorisés pour la création d'utilisateurs via API
 * Aligné avec le backend (CreateUserDto/InviteUserDto)
 * 
 * Note: 'super_admin' et 'admin' ne peuvent PAS être créés via API
 */
export type CreatableRole = 'accountant' | 'manager' | 'analyst' | 'audit' | 'viewer';

/**
 * Requête de création d'utilisateur
 * Aligné avec le backend CreateUserDto
 * 
 * FLUX DE CRÉATION (géré par le backend) :
 * 1. Le frontend envoie cette requête au backend
 * 2. Le backend crée l'utilisateur sur Auth0 via Management API
 * 3. Le backend crée l'utilisateur dans la DB locale avec le lien Auth0
 * 4. Le backend assigne les rôles Auth0 correspondants
 * 5. Un email d'invitation est envoyé à l'utilisateur
 * 
 * @see POST /users
 */
export interface CreateUserRequest {
  // Informations de base (required)
  email: string;
  firstName: string;
  lastName: string;
  
  // Rôle (required) - uniquement les rôles autorisés
  role: CreatableRole;
  
  // organizationId (required) - récupéré depuis les données de l'admin connecté
  // Le backend peut l'ignorer et forcer celui de l'admin, mais il doit être présent
  organizationId: string;
  
  // Unité d'affaires (optional)
  businessUnitId?: string;
  businessUnitType?: 'company' | 'branch' | 'pos';
  
  // Permissions et métadonnées (optional)
  permissions?: string[];
  phone?: string;
  position?: string;
  metadata?: Record<string, any>;
}

export interface UpdateUserRequest {
  firstName?: string;
  lastName?: string;
  role?: CreatableRole;
  status?: 'active' | 'inactive' | 'pending';
  avatar?: string;
  permissions?: string[];
  businessUnitId?: string;
  businessUnitType?: 'company' | 'branch' | 'pos';
  phone?: string;
  position?: string;
}

export interface UsersListResponse {
  users: UserWithDetails[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
}

/**
 * Requête d'invitation d'utilisateur
 * Aligné avec le backend InviteUserDto
 * 
 * Alternative à createUser - envoie directement un email d'invitation
 * L'utilisateur définira son propre mot de passe via Auth0
 * 
 * @see POST /users/invite
 */
export interface UserInviteRequest {
  // Informations de base (required)
  email: string;
  firstName: string;
  lastName: string;
  
  // Rôle (required) - uniquement les rôles autorisés
  role: CreatableRole;
  
  // Permissions et position (optional)
  permissions?: string[];
  position?: string;
}

// Types pour la gestion des accès business unit
export interface UserBusinessUnitAccessRequest {
  businessUnitId: string;
  businessUnitType: 'company' | 'branch' | 'pos';
  accessLevel: 'read' | 'write' | 'admin';
}

export const usersApi = {
  /**
   * Obtenir tous les utilisateurs de l'entreprise
   * @see GET /users
   */
  getUsers: (params?: {
    page?: number;
    pageSize?: number;
    role?: 'admin' | 'accountant' | 'viewer';
    status?: 'active' | 'inactive' | 'pending';
    search?: string;
    businessUnitId?: string;
    accessLevel?: UserAccessLevel;
  }): Promise<ApiResponse<UsersListResponse>> => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.pageSize) searchParams.append('pageSize', params.pageSize.toString());
    if (params?.role) searchParams.append('role', params.role);
    if (params?.status) searchParams.append('status', params.status);
    if (params?.search) searchParams.append('search', params.search);
    if (params?.businessUnitId) searchParams.append('businessUnitId', params.businessUnitId);
    if (params?.accessLevel) searchParams.append('accessLevel', params.accessLevel);
    
    const query = searchParams.toString();
    return ApiService.get<UsersListResponse>(`/users${query ? `?${query}` : ''}`);
  },

  /**
   * Obtenir un utilisateur par ID
   * @see GET /users/{userId}
   */
  getUserById: (userId: string): Promise<ApiResponse<UserWithDetails>> =>
    ApiService.get<UserWithDetails>(`/users/${userId}`),

  /**
   * Créer un nouvel utilisateur
   * @see POST /users
   */
  createUser: (userData: CreateUserRequest): Promise<ApiResponse<UserWithDetails>> =>
    ApiService.post<UserWithDetails>('/users', userData),

  /**
   * Mettre à jour un utilisateur
   * @see PUT /users/{userId}
   */
  updateUser: (userId: string, userData: UpdateUserRequest): Promise<ApiResponse<UserWithDetails>> =>
    ApiService.put<UserWithDetails>(`/users/${userId}`, userData),

  /**
   * Supprimer un utilisateur définitivement
   * @see DELETE /users/{userId}
   */
  deleteUser: (userId: string): Promise<ApiResponse<{ message: string }>> =>
    ApiService.delete<{ message: string }>(`/users/${userId}`),

  /**
   * Désactiver un utilisateur (soft delete)
   * @see PUT /users/{userId}/deactivate
   */
  deactivateUser: (userId: string): Promise<ApiResponse<{ success: boolean; message: string }>> =>
    ApiService.put<{ success: boolean; message: string }>(`/users/${userId}/deactivate`, {}),

  /**
   * Activer un utilisateur précédemment désactivé
   * @see PATCH /users/{userId}/activate
   */
  activateUser: (userId: string): Promise<ApiResponse<{ success: boolean; data: UserWithDetails }>> =>
    ApiService.patch<{ success: boolean; data: UserWithDetails }>(`/users/${userId}/activate`, {}),

  /**
   * Inviter un utilisateur par email
   * @see POST /users/invite
   */
  inviteUser: (inviteData: UserInviteRequest): Promise<ApiResponse<{ success: boolean; data: UserWithDetails; message: string }>> =>
    ApiService.post<{ success: boolean; data: UserWithDetails; message: string }>('/users/invite', inviteData),

  /**
   * Réinviter un utilisateur en statut pending
   * @see POST /users/{userId}/reinvite
   */
  reinviteUser: (userId: string): Promise<ApiResponse<{ success: boolean; message: string }>> =>
    ApiService.post<{ success: boolean; message: string }>(`/users/${userId}/reinvite`, {}),

  /**
   * Obtenir les statistiques des utilisateurs
   * @see GET /users/stats
   */
  getUsersStats: (): Promise<ApiResponse<{ success: boolean; data: UsersStats }>> =>
    ApiService.get<{ success: boolean; data: UsersStats }>('/users/stats'),

  /**
   * Obtenir la liste des permissions disponibles
   * @see GET /users/permissions
   */
  getAvailablePermissions: (): Promise<ApiResponse<{ permissions: AvailablePermission[] }>> =>
    ApiService.get<{ permissions: AvailablePermission[] }>('/users/permissions'),

  /**
   * Obtenir les permissions d'un utilisateur spécifique
   */
  getUserPermissions: (userId: string): Promise<ApiResponse<{ permissions: string[] }>> =>
    ApiService.get<{ permissions: string[] }>(`/users/${userId}/permissions`),

  /**
   * Mettre à jour les permissions d'un utilisateur
   */
  updateUserPermissions: (userId: string, permissions: string[]): Promise<ApiResponse<{ permissions: string[] }>> =>
    ApiService.put<{ permissions: string[] }>(`/users/${userId}/permissions`, { permissions }),

  /**
   * Réinitialiser le mot de passe d'un utilisateur
   * Déclenche l'envoi d'un email de réinitialisation via Auth0
   */
  resetUserPassword: (userId: string): Promise<ApiResponse<{ resetToken: string; expiresAt: string }>> =>
    ApiService.post<{ resetToken: string; expiresAt: string }>(`/users/${userId}/reset-password`, {}),

  // ============================================
  // GESTION DES ACCÈS AUX UNITÉS COMMERCIALES
  // ============================================

  /**
   * Obtenir les accès aux unités commerciales d'un utilisateur
   */
  getUserBusinessUnitAccess: (userId: string): Promise<ApiResponse<UserBusinessUnitAccess[]>> =>
    ApiService.get<UserBusinessUnitAccess[]>(`/users/${userId}/business-units`),

  /**
   * Définir les accès aux unités commerciales d'un utilisateur
   */
  setUserBusinessUnitAccess: (
    userId: string, 
    access: UserBusinessUnitAccessRequest[]
  ): Promise<ApiResponse<UserBusinessUnitAccess[]>> =>
    ApiService.put<UserBusinessUnitAccess[]>(`/users/${userId}/business-units`, { access }),

  /**
   * Ajouter un accès à une unité commerciale
   */
  addUserBusinessUnitAccess: (
    userId: string, 
    access: UserBusinessUnitAccessRequest
  ): Promise<ApiResponse<UserBusinessUnitAccess>> =>
    ApiService.post<UserBusinessUnitAccess>(`/users/${userId}/business-units`, access),

  /**
   * Supprimer un accès à une unité commerciale
   */
  removeUserBusinessUnitAccess: (
    userId: string, 
    businessUnitId: string
  ): Promise<ApiResponse<{ message: string }>> =>
    ApiService.delete<{ message: string }>(`/users/${userId}/business-units/${businessUnitId}`),

  /**
   * Obtenir l'historique d'activité d'un utilisateur
   */
  getUserActivity: (userId: string, params?: {
    page?: number;
    pageSize?: number;
    from?: string;
    to?: string;
  }): Promise<ApiResponse<{
    activities: Array<{
      id: string;
      action: string;
      resource: string;
      timestamp: string;
      details?: Record<string, any>;
    }>;
    pagination: {
      page: number;
      pageSize: number;
      total: number;
      totalPages: number;
    };
  }>> => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.pageSize) searchParams.append('pageSize', params.pageSize.toString());
    if (params?.from) searchParams.append('from', params.from);
    if (params?.to) searchParams.append('to', params.to);
    
    const query = searchParams.toString();
    return ApiService.get(`/users/${userId}/activity${query ? `?${query}` : ''}`);
  },

  // ============================================
  // MÉTHODES DÉPRÉCIÉES (maintenues pour rétrocompatibilité)
  // ============================================

  /**
   * @deprecated Utiliser deactivateUser ou activateUser à la place
   */
  toggleUserStatus: (userId: string): Promise<ApiResponse<UserWithDetails>> =>
    ApiService.patch<UserWithDetails>(`/users/${userId}/toggle-status`, {})
};
